import {Injectable, effect, inject} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {AppToastService} from "@app/shared/services/app-toast.service";
import {ActivatedRoute, Router} from "@angular/router";
import {NgxSpinnerService} from "ngx-spinner";
import {ITableConfig} from "@app/core/components/table/table-config.type";
import {Subject} from "rxjs";
import {WithPagination} from "@app/shared/types/with-pagination.type";
import {appRoutes} from "@app/shared/constants/routes";
import {UserService} from "@app/shared/services/user.service";

import {Role} from "@app/shared/types/role.enum";
import {MatDialog} from "@angular/material/dialog";
import {checkIsToday} from "@app/shared/utils/date-utils";
import {RouteService} from "@app/shared/services/route.service";

import {ReminderDialogComponent} from "@app/core/components/reminder-dialog/reminder-dialog.component";
// types
import {IUserData} from "@app/shared/types/employee.type";
import {ISaleLine, ISaleLineCreate} from "../types/line.type";

const prefix = "capillary-sales-lines";

@Injectable()
export class SaleLineManagementService {
  public updateList$ = new Subject();
  public updateSaleLineDetail$ = new Subject();
  private _user: IUserData;
  private _userRoles: Role[];

  // dependencies
  private _httpClient = inject(HttpClient);
  private _toast = inject(AppToastService);
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _spinner = inject(NgxSpinnerService);
  private _routeService = inject(RouteService);
  private _userService = inject(UserService);
  private _dialog = inject(MatDialog);

  constructor() {
    this._userService.user$.subscribe((user) => (this._user = user));

    effect(() => {
      this._userRoles = this._userService.roles();
    });
  }

  public getAllSaleLines() {
    return this._httpClient.get<WithPagination<ISaleLine>>(
      prefix.concat(this._routeService.getUrlQueryString())
    );
  }

  public getSaleLine(id: string) {
    return this._httpClient.get<ISaleLine>(`${prefix}/${id}`);
  }

  private _createSaleLine(data: ISaleLineCreate) {
    return this._httpClient.post(prefix, data);
  }

  private _updateSaleLine(data: Partial<ISaleLineCreate>, id: string) {
    return this._httpClient.put(`${prefix}/${id}`, data);
  }

  private _bulkArchive() {
    return this._httpClient.put(`${prefix}/archive/bulk`, {});
  }

  public getSaleLinesProcessingPdf() {
    // @ts-ignore
    // prettier-ignore

    return this._httpClient.get<Blob>(`${prefix}/report/lines-pdf`, {observe: "response",responseType: "blob",});
  }

  public getSaleLinesTableConfig(
    saleLines: WithPagination<ISaleLine>
  ): ITableConfig<ISaleLine> {
    return {
      name: "lines",
      displayedColumns: [
        "saleLineTitle",
        "saleLineNumber",
        "description",
        "actions",
      ],
      originalSource: JSON.parse(JSON.stringify(saleLines?.data || {})),
      dataSource: saleLines?.data?.map((item) => ({
        ...item,
        saleLineTitle: item.title,
        saleLineNumber: item.line_number,
        description:
          item?.description?.length > 20
            ? item?.description?.slice(0, 20).concat("...")
            : item?.description || "-",
      })),
      pagination: {
        count: saleLines.count,
        page: this._activatedRoute.snapshot.queryParams["page"],
        pageSize: this._activatedRoute.snapshot.queryParams["page-size"],
      },
      minWidth: "850px",
      columnsWidths: ["25%", "15%", "25%", "30%"],
      columnsCssClasses: {
        created_at: (line) =>
          checkIsToday(line.created_at) ? "!text-success font-semibold" : "",
      },
      columnsIcons: {},
      actions: [
        {
          icon: "visibility",
          newPageUrl: (line) =>
            `/${appRoutes.capillary}/line-management/${line.id}`,
          handler: (line) => this.navigateToViewPage(line.id),
          cssClass: "!text-info",
        },
        {
          icon: "edit",
          handler: (line) => this.navigateToEditPage(line.id),
          cssClass: "!text-success",
          if: () => !!this._userRoles?.includes(Role.UpdateCapillarySalesLine),
          disabled: (line) => this._checkCanEditSaleLine(line),
        },
        {
          icon: "notifications",
          handler: (line) => this._openReminderDialog("", line?.id),
          cssClass: "!text-warning",
        },
      ],
    };
  }

  private _checkCanEditSaleLine(line: any) {
    if (this._userRoles && this._userRoles.includes(Role.FullAccess)) {
      return false;
    } else if (this._userRoles?.includes(Role.UpdateCapillarySalesLine)) {
      return false;
    } else {
      return true;
    }
  }

  public navigateToViewPage(id: string) {
    this._router.navigate([`/${appRoutes.capillary}/line-management/${id}`], {
      queryParamsHandling: "merge",
    });
  }

  public navigateToEditPage(id: string) {
    this._router.navigate(
      [`/${appRoutes.capillary}/line-management/${id}/edit`],
      {
        queryParamsHandling: "merge",
      }
    );
  }

  public createNewSaleLine(data: ISaleLineCreate) {
    this._spinner.show();
    this._createSaleLine(data).subscribe({
      next: () => {
        this._toast.success(
          "capillary.lineManagement.create.newLineCreationSuccessfulMessage"
        );
        this._router.navigate([`/${appRoutes.capillary}/line-management`], {
          queryParamsHandling: "merge",
        });
      },
      error: () => this._spinner.hide(),
      complete: () => this._spinner.hide(),
    });
  }

  public updateSaleLine(data: Partial<ISaleLineCreate>, id: string) {
    this._spinner.show();
    this._updateSaleLine(data, id).subscribe({
      next: () => {
        this._toast.success(
          "capillary.lineManagement.edit.saleLineUpdatedSuccessfulMessage"
        );
        this._router.navigate([`/${appRoutes.capillary}/line-management`], {
          queryParamsHandling: "merge",
        });
      },
      error: () => this._spinner.hide(),
      complete: () => this._spinner.hide(),
    });
  }

  private _openReminderDialog(customerId: string, lineId: string) {
    const dialogRef = this._dialog.open<ReminderDialogComponent>(
      ReminderDialogComponent,
      {
        width: "100%",
        maxWidth: "500px",
        data: {customerId: customerId, lineId: lineId},
      }
    );

    dialogRef.afterClosed().subscribe((data) => {
      return;
    });
  }
}
